import { StorefrontApiError, submitReview } from '../../api/client';
import type { ReviewWriteWords } from './reviewWriteWords';

// A shopper's review before it is sent: what the form holds, what it still lacks, and how sending it went. The
// server's schema is the authority; these checks only spare the shopper a round trip for an empty field.

/** What the review form holds while the shopper writes. */
export type ReviewDraft = { rating: number; name: string; phone: string; body: string; photos: readonly string[] };

/** The fields a shopper must fill before a review may go. */
export type DraftField = 'rating' | 'name' | 'body';

/** Where sending stands: not yet, on its way, kept for the shop to read, or refused and why. */
export type DraftSending = 'idle' | 'sending' | 'sent' | 'failed' | 'tooMany';

/** A form nobody has touched. */
export const EMPTY_DRAFT: ReviewDraft = { rating: 0, name: '', phone: '', body: '', photos: [] };

/** The most a name and a review may hold, the server's own bounds. */
export const NAME_MAX = 120;
export const BODY_MAX = 5000;
export const PHONE_MAX = 30;

// Lists what the draft still lacks, each with the sentence the shopper reads under that field.
export function draftProblems(draft: ReviewDraft, words: ReviewWriteWords): Partial<Record<DraftField, string>> {
  const problems: Partial<Record<DraftField, string>> = {};
  if (draft.rating < 1 || draft.rating > 5) problems.rating = words.needRating;
  if (draft.name.trim() === '') problems.name = words.needName;
  if (draft.body.trim() === '') problems.body = words.needReview;
  return problems;
}

// Keeps the shown problems still unfixed: a flagged field clears once right, and none is flagged before the next send.
export function stillWrong(shown: Partial<Record<DraftField, string>>, found: Partial<Record<DraftField, string>>): Partial<Record<DraftField, string>> {
  return Object.fromEntries(Object.entries(found).filter(([field]) => field in shown));
}

// Sends the draft as a review of the product and answers how it went; a 429 is told apart so the shopper waits.
export async function sendDraft(productId: string, draft: ReviewDraft): Promise<DraftSending> {
  try {
    await submitReview({
      productId, rating: draft.rating, customerName: draft.name.trim(), customerPhone: draft.phone.trim() || undefined,
      body: draft.body.trim(), images: [...draft.photos]
    });
    return 'sent';
  } catch (error) {
    return error instanceof StorefrontApiError && error.status === 429 ? 'tooMany' : 'failed';
  }
}
